import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  Input,
  Popover,
  PopoverAnchor,
  PopoverContent,
  Typography,
} from "@/components/ui";
import { retrieveGlobalSearchQueryOptions } from "@/lib/data/react-query/search/global-search";
import { cn } from "@/lib/utils/cn";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  BriefcaseBusiness,
  LoaderCircle,
  Search,
  UserRound,
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";

const SEARCH_DEBOUNCE_MS = 250;

const GLOBAL_SEARCH_INPUT_CLASS_NAME =
  "text-sidebar-foreground placeholder:text-sidebar-foreground/40 border-sidebar-border bg-background/60 focus-visible:border-ring border pl-9 shadow-sm focus-visible:ring-2 focus-visible:ring-offset-0";

const resultSectionTitle: Record<"matter" | "contact", string> = {
  contact: "Contacts",
  matter: "Matters",
};

export function GlobalMatterSearch() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchText(searchText.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchText]);

  const globalSearchQuery = useQuery(
    retrieveGlobalSearchQueryOptions({
      query: debouncedSearchText,
      limit: 16,
    }),
  );

  const results = useMemo(
    () => globalSearchQuery.data?.data ?? [],
    [globalSearchQuery.data?.data],
  );
  const groupedResults = useMemo(
    () => ({
      contacts: results.filter((result) => result.entityType === "contact"),
      matters: results.filter((result) => result.entityType === "matter"),
    }),
    [results],
  );

  const isFetching = globalSearchQuery.isFetching;
  const isError = globalSearchQuery.isError;
  const hasQuery = debouncedSearchText.length >= 2;
  const shouldShowDropdown = isOpen && searchText.trim().length >= 2;

  const helperText = useMemo(() => {
    if (!hasQuery) {
      return "Type at least 2 characters to search matters and contacts.";
    }

    if (isFetching) {
      return "Searching matters and contacts...";
    }

    if (isError) {
      return "Unable to search right now.";
    }

    return null;
  }, [hasQuery, isError, isFetching]);

  const openIfEligible = () => {
    if ((inputRef.current?.value ?? "").trim().length >= 2) {
      setIsOpen(true);
    }
  };

  const handleSelect = async (result: (typeof results)[number]) => {
    setIsOpen(false);
    setSearchText("");
    setDebouncedSearchText("");

    if (result.entityType === "matter") {
      await navigate({ to: `/matters/${result.entityId}` });
      return;
    }

    await navigate({ to: `/contacts/${result.entityId}` });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  return (
    <Popover open={shouldShowDropdown} onOpenChange={setIsOpen}>
      <div className="w-full max-w-80">
        <PopoverAnchor asChild>
          <div className="relative">
            <Search className="text-sidebar-foreground/40 pointer-events-none absolute top-1/2 left-3 z-10 h-4 w-4 -translate-y-1/2" />
            <Input
              maxLength={200}
              ref={inputRef}
              type="search"
              value={searchText}
              placeholder="Search matters and contacts..."
              aria-label="Global search"
              autoComplete="off"
              className={GLOBAL_SEARCH_INPUT_CLASS_NAME}
              onFocus={openIfEligible}
              onChange={(event) => {
                const nextValue = event.target.value;
                setSearchText(nextValue);
                setIsOpen(nextValue.trim().length >= 2);
              }}
              onKeyDown={handleKeyDown}
            />
          </div>
        </PopoverAnchor>

        <PopoverContent
          align="start"
          side="bottom"
          sideOffset={8}
          className="border-border bg-popover text-popover-foreground w-[360px] rounded-2xl border p-0 shadow-[0_18px_40px_rgba(15,23,42,0.16)]"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
          }}
          onInteractOutside={(event) => {
            if (inputRef.current?.contains(event.target as Node)) {
              event.preventDefault();
            }
          }}
        >
          <Command shouldFilter={false} className="bg-popover rounded-2xl">
            <CommandList className="max-h-[420px]">
              {helperText ? (
                <div className="flex items-center gap-2 px-3 py-3">
                  {isFetching ? (
                    <LoaderCircle className="text-muted-foreground h-4 w-4 animate-spin" />
                  ) : null}
                  <Typography className="text-muted-foreground text-sm">
                    {helperText}
                  </Typography>
                </div>
              ) : null}

              {!helperText && results.length === 0 ? (
                <CommandEmpty>No results found.</CommandEmpty>
              ) : null}

              {!helperText && groupedResults.matters.length > 0 ? (
                <CommandGroup
                  heading={resultSectionTitle.matter}
                  className="border-border [&_[cmdk-group-heading]]:text-muted-foreground border-b px-0 pb-3 [&_[cmdk-group-heading]]:px-4 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:pb-2 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:tracking-[0.08em] [&_[cmdk-group-heading]]:uppercase"
                >
                  {groupedResults.matters.map((result) => (
                    <CommandItem
                      key={`${result.entityType}-${result.entityId}`}
                      value={`${result.entityType}-${result.entityId}`}
                      className="text-foreground data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground data-[selected=true]:[&_.text-foreground]:text-accent-foreground data-[selected=true]:[&_.text-muted-foreground]:text-accent-foreground mx-2 min-h-14 items-center gap-3 rounded-xl px-3 py-2"
                      onSelect={() => {
                        void handleSelect(result);
                      }}
                    >
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-600">
                        <BriefcaseBusiness className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <Typography className="text-foreground truncate text-sm font-medium">
                          {result.title}
                        </Typography>
                        <Typography className="text-muted-foreground mt-0.5 text-xs">
                          {result.subtitle || "Matter"}
                        </Typography>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ) : null}

              {!helperText && groupedResults.contacts.length > 0 ? (
                <CommandGroup
                  heading={resultSectionTitle.contact}
                  className={cn(
                    "[&_[cmdk-group-heading]]:text-muted-foreground px-0 pb-3 [&_[cmdk-group-heading]]:px-4 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:pb-2 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:tracking-[0.08em] [&_[cmdk-group-heading]]:uppercase",
                    groupedResults.matters.length > 0 ? "pt-0" : "",
                  )}
                >
                  {groupedResults.contacts.map((result) => (
                    <CommandItem
                      key={`${result.entityType}-${result.entityId}`}
                      value={`${result.entityType}-${result.entityId}`}
                      className="text-foreground data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground data-[selected=true]:[&_.text-foreground]:text-accent-foreground data-[selected=true]:[&_.text-muted-foreground]:text-accent-foreground mx-2 min-h-14 items-center gap-3 rounded-xl px-3 py-2"
                      onSelect={() => {
                        void handleSelect(result);
                      }}
                    >
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-600">
                        <UserRound className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <Typography className="text-foreground truncate text-sm font-medium">
                          {result.title}
                        </Typography>
                        <Typography className="text-muted-foreground mt-0.5 text-xs">
                          {result.subtitle || "Contact"}
                        </Typography>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ) : null}
            </CommandList>
          </Command>
        </PopoverContent>
      </div>
    </Popover>
  );
}
